import React, { useState, useEffect } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tab,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Tabs,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { formatDistanceToNowStrict } from 'date-fns'
import axios from 'axios'
import { FEEDBACK_CATEGORIES } from '../../types/feedbackCategory'

interface FeedbackItem {
  id: number
  hidden: boolean
  subject: string
  message: string
  status: 'new' | 'in_progress' | 'resolved' | 'no_action'
  created_at: string
  user_name: string
  assigned_name: string | null
  funder_name?: string
  opportunity_name?: string
  comment_count: number
  assigned_to?: string
}

interface AdminUser {
  id: string
  name: string
}

// API calls for real data
const fetchFeedback = async (
  statusFilter?: string,
  assigneeFilter?: string,
  categoryFilter?: string
): Promise<FeedbackItem[]> => {
  try {
    // Build query parameters
    const params = new URLSearchParams()
    if (statusFilter) {
      params.append('status', statusFilter)
    }
    if (assigneeFilter === 'unassigned') {
      params.append('assigned_to', 'null')
    } else if (assigneeFilter) {
      params.append('assigned_to', assigneeFilter)
    }

    if (categoryFilter) {
      params.append('category', categoryFilter)
    }

    const apiUrl = import.meta.env.VITE_API_URL
    const response = await axios.get(`${apiUrl}/feedback?${params.toString()}`)
    return response.data
  } catch (error) {
    console.error('Error fetching feedback:', error)
    return []
  }
}

// Get admin users
const fetchAdminUsers = async (): Promise<AdminUser[]> => {
  try {
    const apiUrl = import.meta.env.VITE_API_URL

    // VITE_API_URL already includes '/v1/admin', so the full path is '/v1/admin/users?role=admin'
    // Using the direct role parameter which matches our backend implementation
    const response = await axios.get(`${apiUrl}/users/?role=admin`)

    if (!response.data || !Array.isArray(response.data)) {
      console.error('Unexpected response format:', response.data)
      return []
    }

    return response.data.map((user: any) => ({
      id: user.uuid,
      name:
        `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email,
    }))
  } catch (error: any) {
    console.error('Error fetching admin users:', error)
    // Log more details about the error
    if (error.response) {
      console.error('Response status:', error.response.status)
      console.error('Response data:', error.response.data)
    }
    return []
  }
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'new':
      return 'error'
    case 'in_progress':
      return 'warning'
    case 'resolved':
      return 'success'
    case 'no_action':
      return 'default'
    default:
      return 'default'
  }
}

export const FeedbackList: React.FC = () => {
  const [tabValue, setTabValue] = useState(0)
  const [feedbackItems, setFeedbackItems] = useState<FeedbackItem[]>([])
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [assigneeFilter, setAssigneeFilter] = useState<string>('')
  const [categoryFilter, setCategoryFilter] = useState<string>('')
  const navigate = useNavigate()

  // Load admin users on component mount
  useEffect(() => {
    const loadAdminUsers = async () => {
      const users = await fetchAdminUsers()
      setAdminUsers(users)
    }

    loadAdminUsers()
  }, [])

  useEffect(() => {
    const loadFeedback = async () => {
      setLoading(true)
      try {
        // Fetch filtered data directly from API
        const data = await fetchFeedback(
          statusFilter,
          assigneeFilter,
          categoryFilter
        )
        setFeedbackItems(data)
      } catch (error) {
        console.error('Error loading feedback:', error)
      } finally {
        setLoading(false)
      }
    }

    loadFeedback()
  }, [statusFilter, assigneeFilter, categoryFilter, tabValue])

  function a11yProps(index: number) {
    return {
      id: `feedback-tab-${index}`,
      'aria-controls': `feedback-tabpanel-${index}`,
    }
  }

  function handleTabChange(e: React.SyntheticEvent, newValue: number) {
    // reset category filter when tab changes
    setCategoryFilter('')
    setTabValue(newValue)
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Feedback Management
      </Typography>
      <Box sx={{ borderBotton: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Match Feedback" {...a11yProps(0)} />
          <Tab label="Match Compliments" {...a11yProps(1)} />
          <Tab label="General Feedback" {...a11yProps(2)} />
          <Tab label="General Compliments" {...a11yProps(3)} />
        </Tabs>
      </Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filters
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControl variant="outlined" sx={{ minWidth: 200 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="new">New</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
                <MenuItem value="no_action">No Action</MenuItem>
              </Select>
            </FormControl>

            <FormControl variant="outlined" sx={{ minWidth: 200 }}>
              <InputLabel>Assigned To</InputLabel>
              <Select
                value={assigneeFilter}
                onChange={(e) => setAssigneeFilter(e.target.value)}
                label="Assigned To"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="unassigned">Unassigned</MenuItem>
                {adminUsers.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {tabValue === 0 && (
              <FormControl variant="outlined" sx={{ minWidth: 200 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  label="Category"
                >
                  <MenuItem value="">All</MenuItem>
                  {FEEDBACK_CATEGORIES.map((category) => (
                    <MenuItem key={category.value} value={category.value}>
                      {category.label}
                    </MenuItem>
                  ))}
                  <MenuItem value="other">Other - No Category</MenuItem>
                </Select>
              </FormControl>
            )}

            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setStatusFilter('')
                setAssigneeFilter('')
              }}
            >
              Reset Filters
            </Button>
          </Box>
        </CardContent>
      </Card>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Opportunity</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell>Assigned To</TableCell>
                <TableCell>Comments</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {feedbackItems.filter((item) => {
                if (tabValue === 0) return item.opportunity_name && item.hidden
                if (tabValue === 1) return item.opportunity_name && !item.hidden
                if (tabValue === 2) return !item.opportunity_name && item.hidden
                if (tabValue === 3)
                  return !item.opportunity_name && !item.hidden
              }).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    No feedback found
                  </TableCell>
                </TableRow>
              ) : (
                feedbackItems
                  .filter((item) => {
                    if (tabValue === 0)
                      return item.opportunity_name && item.hidden
                    if (tabValue === 1)
                      return item.opportunity_name && !item.hidden
                    if (tabValue === 2)
                      return !item.opportunity_name && item.hidden
                    if (tabValue === 3)
                      return !item.opportunity_name && !item.hidden
                  })
                  .map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.id}</TableCell>
                      <TableCell>
                        {item.hidden ? 'Not helpful' : 'Helpful'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.status.replace('_', ' ')}
                          color={getStatusColor(item.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{item.subject}</TableCell>
                      <TableCell>{item.user_name}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {item.funder_name}
                        </Typography>
                        <Typography variant="caption" display="block">
                          {item.opportunity_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {formatDistanceToNowStrict(
                          new Date(item.created_at + 'Z'),
                          {
                            addSuffix: true,
                          }
                        )}
                      </TableCell>
                      <TableCell>
                        {item.assigned_name || <em>Unassigned</em>}
                      </TableCell>
                      <TableCell>{item.comment_count}</TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => navigate(`/feedback/${item.id}`)}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  )
}

export default FeedbackList
